import React, { useState, useMemo } from 'react';

import moment from 'moment';

import classNames from 'classnames';
import ActionsMenu from '../ActionsMenu';
import EditableTitle from '../EditableTitle';
import TableRowAction from '../TableRowAction';

import downloadFolder from '../../../helpers/downloadFolder';
import { ORG_ROLES } from '../../../constants/main';
import folderIcon from '../../../assets/images/my-library/folder.svg';
import viewedIcon from '../../../assets/images/my-library/viewed.svg';
// eslint-disable-next-line import/no-named-as-default
import useAuthorization from '../../../hooks/useAuthorization';
import classes from './styles.module.scss';

const FolderRow = ({
  folder,
  onClick,
  user,
  onUpdateTitle,
  handleDelete,
  handleShare,
  handleManageUsers,
  handleManageAccess,
  handleCopyLink,
  //  classes,
  showReport,
  isTrashBin,
  handleMoveToTrash,
  handleRestore,
  viewedMeetingCount,
  meetingCount,
  handleMove,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // Use authorization hook for permission checks
  const resource = useMemo(() => ({ userId: folder.userId }), [folder.userId]);
  const resourceUser = useMemo(() => 
    folder.userRole ? { role: folder.userRole } : null, 
    [folder.userRole]
  );
  
  const {
    canEditFolder,
    canDeleteFolder,
    canManageFolderPermissions,
    isFolderOwner,
    isOrgOwner,
  } = useAuthorization(
    user,
    resource,
    resourceUser,
    'folder'
  );

  const isOwnerOrCreator = canEditFolder;
  const isNotAllowedToDeleteOrShare = !canDeleteFolder;
  const isNotAllowedToCopyLink = !canEditFolder; // Use centralized permission check - org owners can edit, so they can copy link
  const isNotAllowedToManageUsers = !!folder.parentId || (!canManageFolderPermissions && !(resourceUser?.role === 'contributor'));
  const isNotAllowedToDownload = user?.orgRole?.access !== ORG_ROLES.OWNER;
  const lastPublishedDate = moment(folder.updatedAt).format('MMM D, YYYY');
  const isDeleted = folder.deletedAt;
  const allVideosViewed =
    meetingCount > 0 && viewedMeetingCount === meetingCount;
  const hasUnviewedMeetings = meetingCount > viewedMeetingCount;

  return (
    <tr
      key={folder.id}
      onClick={onClick}
      style={{ cursor: isDeleted ? 'auto' : 'pointer' }}
      className={classes.FolderRow}
    >
      <td className={classes.title}>
        <div className={classes.innerContainer}>
          <div className={classes.icon}>
            <img src={folderIcon} alt="Folder" />
          </div>
          <div
            style={{
              pointerEvents: isDeleted ? 'none' : 'auto',
            }}
          >
            <div className={classes.titleContainer}>
              <EditableTitle
                handleUpdateTitle={onUpdateTitle}
                isEditable={isOwnerOrCreator}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              >
                {folder.name}
              </EditableTitle>
              {hasUnviewedMeetings && <div className={classes.new}>New</div>}
              {/* {hasUnviewedMeetings && (
              <span className={classes.newVideosAdded}>New video(s) added</span>
            )} */}
            </div>
            <span className={classes.publishedDate}>
              modified {lastPublishedDate}
            </span>
          </div>
        </div>
      </td>
      <td className={classes.owner}>
        {folder.userId === user?.id ? 'Me' : folder.Owner.name}
      </td>
      <td className={classes.publishedDate}>{lastPublishedDate}</td>
      <td className={classes.viewedCount}>
        <div className={classes.viewedContainer}>
          <span className={classes.count}>
            {viewedMeetingCount} of {meetingCount}{' '}
          </span>
          {allVideosViewed && (
            <img src={viewedIcon} alt="Viewed" className={classes.viewedMark} />
          )}
        </div>
      </td>
      <td className={classes.actions}>
        <TableRowAction
          noEdit={isTrashBin}
          handleDelete={isTrashBin ? handleDelete : handleMoveToTrash}
          handleEdit={() => setIsEditing(true)}
          handleManageAccess={handleManageAccess ? () => handleManageAccess(folder) : undefined}
          noManageAccess={isTrashBin}
          canEdit={canEditFolder}
          canDelete={canDeleteFolder}
          canManageAccess={canManageFolderPermissions || (resourceUser?.role === 'contributor')}
        />
      </td>
      <td className={classNames(classes.buttons, classes.actions)}>
        <ActionsMenu
          handleDelete={handleDelete}
          handleShare={handleShare}
          handleEdit={() => setIsEditing(true)}
          handleManageUsers={handleManageUsers}
          handleCopyLink={handleCopyLink}
          handleDownload={() => downloadFolder(folder)}
          handleMoveToTrash={handleMoveToTrash}
          handleRestore={handleRestore}
          handleMove={handleMove && !isTrashBin && (isOrgOwner || isFolderOwner) ? () => handleMove(folder) : undefined}
          isNotAllowedToDelete={isNotAllowedToDeleteOrShare}
          isNotAllowedToShare={isNotAllowedToDeleteOrShare}
          isNotAllowedToManageUsers={isNotAllowedToManageUsers}
          isNotAllowedToCopyLink={isNotAllowedToCopyLink}
          isNotAllowedToDownload={isNotAllowedToDownload}
          isNotAllowedToGetViewerReport={false}
          handleGetViewerReport={() => showReport(folder)}
          isTrashBin={isTrashBin}
          isNotAllowedToEdit={!isOwnerOrCreator}
        />
      </td>
    </tr>
  );
};

export default FolderRow;
